import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TermsEditor from "@/components/TermsEditor";
import { RotateCcw } from "lucide-react";

const fromProfile = (p) => ({ enabled: true, supplier_id: p.supplier_id, name: p.name, logo_b64: p.logo_b64,
  partner_line: p.partner_line, quote_title: p.quote_title, terms: (p.terms || []).map((t) => ({ ...t })) });

export const OemBrandingBox = ({ oem, onChange, awardedSupplierIds = [] }) => {
  const [profiles, setProfiles] = useState([]);
  const [standard, setStandard] = useState([]);
  useEffect(() => {
    api.get("/oem-quote-profiles").then((r) => setProfiles(r.data)).catch(() => {});
    api.get("/quote-terms/standard").then((r) => setStandard(r.data)).catch(() => {});
  }, []);

  const o = oem || { enabled: false };
  const byId = (id) => profiles.find((p) => p.supplier_id === id);
  const enable = (on) => {
    if (!on) { onChange({ ...o, enabled: false }); return; }
    const p = byId(o.supplier_id) || profiles.find((x) => awardedSupplierIds.includes(x.supplier_id)) || profiles[0];
    onChange(p ? fromProfile(p) : { ...o, enabled: true });
  };
  const set = (patch) => onChange({ ...o, ...patch });

  return (
    <div className="rounded-lg border border-[#B9CBE4] bg-[#F7FAFE] p-4" data-testid="cq-oem-box">
      <label className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
        <Checkbox checked={!!o.enabled} onCheckedChange={(v) => enable(!!v)} disabled={!profiles.length} data-testid="cq-oem-toggle" />
        Quote As An OEM Partner
        <span className="font-normal text-xs text-muted-foreground">· OEM logo beside Somcore's and OEM terms</span>
      </label>
      {!profiles.length && <p className="mt-1.5 text-[11px] text-muted-foreground">No OEM has approved quote branding yet. Set it up on the OEM partner's page in the CRM (Suppliers → OEM Partner → Client Quote Branding & Terms).</p>}
      {o.enabled && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[14rem] flex-1">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">OEM</div>
              <Select value={o.supplier_id || undefined} onValueChange={(id) => onChange(fromProfile(byId(id)))}>
                <SelectTrigger className="h-8 bg-white" data-testid="cq-oem-select"><SelectValue placeholder="Select OEM" /></SelectTrigger>
                <SelectContent>{profiles.map((p) => <SelectItem key={p.supplier_id} value={p.supplier_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {byId(o.supplier_id) && <Button type="button" variant="outline" size="sm" onClick={() => onChange(fromProfile(byId(o.supplier_id)))} data-testid="cq-oem-reset"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Reset To Approved Wording</Button>}
            {o.supplier_id && <Link to={`/suppliers/${o.supplier_id}`} className="text-xs text-[#1B4383] underline underline-offset-2" data-testid="cq-oem-profile-link">Open OEM Profile</Link>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-600">Partner Line<Input className="mt-1 h-8 bg-white" value={o.partner_line || ""} onChange={(e) => set({ partner_line: e.target.value })} data-testid="cq-oem-partner-line" /></label>
            <label className="text-xs text-slate-600">Quote Title<Input className="mt-1 h-8 bg-white" value={o.quote_title || ""} onChange={(e) => set({ quote_title: e.target.value })} data-testid="cq-oem-title" /></label>
          </div>
          <div>
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Terms For This Quote Only · The OEM's Approved Profile Is Not Changed</div>
            <TermsEditor standard={standard} value={o.terms} onChange={(t) => set({ terms: t })} />
          </div>
        </div>
      )}
    </div>
  );
};

export default OemBrandingBox;
